import React, { useMemo } from 'react';
import { Box, Link, Stack, Flex, Badge, Text, Divider } from '@chakra-ui/react';
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons';

import { Icon } from 'shared-components';
import RecordedInterviewFulfilmentRenderer from './requirement-rows/fulfilment-renderers/RecordedInterviewFulfilmentRenderer';
import {
  getAcademicResultsFromStudyHistory,
  getAcademicTranscriptFromApplication,
  getEnglishLanguageCertificateFromApplication,
  getEnglishLanguageResultsFromApplication,
  getProofOfManagerialExperienceFromApplication,
  getFulfilmentProp,
  getRefereeRelationshipText,
  IEnglishTestData,
} from '@/utilities/fulfilmentData';
import {
  ApplicantOfferRequirementTask,
  Application,
  EnglishTestType,
  IAcademicResultFulfilmentData,
  IApplicantOfferRequirementTask,
  IDocumentFulfilmentData,
  IReferee,
  ITextFulfilmentData,
} from 'types';

export const PreviewLink = ({
  preview,
  text,
  urls,
  subtext,
  count,
}: {
  preview: (urls: string[]) => void;
  text: string;
  urls: string[];
  subtext?: string;
  count?: number;
}): React.ReactElement => {
  return (
    <Flex alignItems="start" gap={2}>
      <Stack spacing={1}>
        <Link
          onClick={() => {
            try {
              preview(urls);
            } catch (error) {
              console.error('Error calling preview function:', error);
            }
          }}
          fontSize={'inherit'}
          lineHeight={6}
        >
          {text}
          <Icon icon={faPaperclip} fontSize="sm" height={6} ml={2} verticalAlign={'top'} />
          {typeof count === 'number' && count > 1 && (
            <Badge variant="pill" size={'md'} minWidth={5} verticalAlign={'top'} ml={1} mt={0.5}>
              {count}
            </Badge>
          )}
        </Link>

        {subtext && (
          <Box color="muted" fontSize={'sm'} lineHeight={4}>
            {subtext}
          </Box>
        )}
      </Stack>
    </Flex>
  );
};

export const FormattedText = ({ text, subtext }: { text: string; subtext?: string }): React.ReactElement => {
  const textMultiline = text.split('/n');
  return (
    <Stack spacing={1}>
      {textMultiline.map((textLine, index) => (
        <Box key={index} fontWeight={'medium'}>
          {textLine}
        </Box>
      ))}
      {subtext?.split('\n').map((line, index) => (
        <Box key={index} color="muted" fontSize={'sm'} lineHeight={4}>
          {line}
        </Box>
      ))}
    </Stack>
  );
};

// Helper function to get the qualification level label from YHS data
const getQualificationLevelLabel = (highest: Application['studyHistory']['highest']): string | undefined => {
  if (!highest) return undefined;

  // Try to get from qualificationTypeSelections first (new YHS structure)
  if (highest.qualificationTypeSelections && highest.qualificationTypeSelections.length > 0) {
    const firstSelection = highest.qualificationTypeSelections[0];
    // If it's "Other", use the custom name
    if (firstSelection.qualificationTypeLabel === 'Other' && firstSelection.customQualificationTypeName) {
      return firstSelection.customQualificationTypeName;
    }
    // Otherwise use the label
    if (firstSelection.qualificationTypeLabel) {
      return firstSelection.qualificationTypeLabel;
    }
  }

  // Try to get from first qualification (fallback)
  if (highest.qualifications) {
    const firstQual = Object.values(highest.qualifications)[0];
    if (firstQual?.qualificationTypeLabel) {
      return firstQual.qualificationTypeLabel;
    }
    if (firstQual?.otherQualificationName) {
      return firstQual.otherQualificationName;
    }
  }

  // Final fallback to level field (old structure)
  return highest.level;
};

// Helper function to generate linkText based on requirement type and application data
const generateLinkText = (
  requirementId: ApplicantOfferRequirementTask | string,
  application: Application,
  originalFulfilmentData: IApplicantOfferRequirementTask['fulfilmentData'],
  taskText?: string,
  taskType?: ApplicantOfferRequirementTask,
): string | undefined => {
  const documentData = originalFulfilmentData as IDocumentFulfilmentData;
  const switchKey = taskType ?? requirementId;

  switch (switchKey) {
    case ApplicantOfferRequirementTask.Passport: {
      if (application.nationality) {
        return `${application.nationality} passport`;
      }
      return 'Passport';
    }
    case ApplicantOfferRequirementTask.PhotoID: {
      if (application.nationality) {
        return `${application.nationality} photo ID`;
      }
      return 'Photo ID';
    }
    case ApplicantOfferRequirementTask.CV: {
      return 'CV/Resume';
    }
    case ApplicantOfferRequirementTask.LetterOfRecommendation: {
      return 'Reference letter';
    }
    case ApplicantOfferRequirementTask.AcademicTranscript: {
      if (application.studyHistory.highest) {
        const levelLabel = getQualificationLevelLabel(application.studyHistory.highest);
        return `${application.studyHistory.highest.institutionName} ${levelLabel || 'Unknown level'} transcript`;
      }
      return 'Academic transcript';
    }
    case ApplicantOfferRequirementTask.AcademicCertificate: {
      if (application.studyHistory.highest) {
        const levelLabel = getQualificationLevelLabel(application.studyHistory.highest);
        return `${application.studyHistory.highest.institutionName} ${levelLabel || 'Unknown level'} certificate`;
      }
      return 'Academic certificate';
    }
    case ApplicantOfferRequirementTask.EnglishLanguageCertificate: {
      if (application.englishTest.hasTakenTest && application.englishTest.type) {
        if (application.englishTest.type === EnglishTestType.Other) {
          return `${application.englishTest.typeOther} ${application.englishTest.score}`;
        }
        return `${application.englishTest.type} results`;
      }
      return 'English language certificate';
    }
    case ApplicantOfferRequirementTask.PersonalStatement: {
      return 'Personal statement';
    }
    case ApplicantOfferRequirementTask.Portfolio: {
      return 'Portfolio';
    }
    case ApplicantOfferRequirementTask.ProofOfManagerialExperience: {
      return 'Proof of managerial experience';
    }
    case ApplicantOfferRequirementTask.ProofOfAddress: {
      return 'Proof of address';
    }
    case ApplicantOfferRequirementTask.ProofOfImmigrationStatus: {
      return 'Proof of immigration status';
    }
    case ApplicantOfferRequirementTask.NameAffidavit: {
      return 'Name affidavit';
    }
    case ApplicantOfferRequirementTask.ProofOfNameChange: {
      return 'Proof of name change';
    }
    case ApplicantOfferRequirementTask.BirthCert: {
      return 'Birth certificate';
    }
    case ApplicantOfferRequirementTask.ResearchProposal: {
      return 'Research proposal';
    }
    default: {
      // For custom requirements, use the task title provided by the parent component
      if (taskText) {
        return taskText;
      }
      // Fall back to s3Key filename for unknown types
      return documentData?.sKey?.split('#').pop();
    }
  }
};

const getFormattedFulfilmentData = (
  requirementId: ApplicantOfferRequirementTask | string,
  application: Application,
  originalFulfilmentData: IApplicantOfferRequirementTask['fulfilmentData'] & {
    englishTest?: IEnglishTestData;
  },
  preview?: (urls: string[]) => void,
  taskText?: string,
  isMultiQualification?: boolean,
  taskType?: ApplicantOfferRequirementTask,
): React.ReactElement | null | undefined => {
  // Use taskType when available so suffixed IDs (e.g. "AcademicResults-abc123") still match.
  const switchKey = taskType ?? requirementId;
  switch (switchKey) {
    case ApplicantOfferRequirementTask.FinalAcademicResults:
    case ApplicantOfferRequirementTask.AcademicResults: {
      const academicFulfilmentData = originalFulfilmentData as IAcademicResultFulfilmentData | undefined;
      const academicResults = getAcademicResultsFromStudyHistory(
        academicFulfilmentData?.studyHistory,
        isMultiQualification,
      );
      if (academicResults.length === 0) {
        return null;
      }
      return (
        <Stack spacing={2}>
          {academicResults.map(({ text, subtext }, index) => (
            <FormattedText key={index} text={text} subtext={subtext} />
          ))}
        </Stack>
      );
    }

    case ApplicantOfferRequirementTask.EnglishLanguageResults: {
      if (!originalFulfilmentData.englishTest) {
        return null;
      }
      const { text, subtext } = getEnglishLanguageResultsFromApplication(
        originalFulfilmentData.englishTest,
        application,
      );
      return <FormattedText text={text} subtext={subtext} />;
    }

    case ApplicantOfferRequirementTask.AcademicTranscript: {
      const documentData = originalFulfilmentData as IDocumentFulfilmentData;
      if (documentData?.urls) {
        const linkText = generateLinkText(requirementId, application, originalFulfilmentData, taskText, taskType);
        const count = documentData?.urls?.length || 0; // show total pages across all files
        return (
          <PreviewLink
            text={linkText || 'Academic transcript'}
            preview={preview}
            urls={documentData.urls}
            count={count}
          />
        );
      }

      const academicTranscript = getAcademicTranscriptFromApplication(application);
      if (
        academicTranscript &&
        originalFulfilmentData &&
        typeof originalFulfilmentData === 'object' &&
        'urls' in originalFulfilmentData &&
        Array.isArray(originalFulfilmentData.urls)
      ) {
        const { text } = academicTranscript;
        const urls = getFulfilmentProp(originalFulfilmentData, 'urls');
        return <PreviewLink text={text} preview={preview} urls={Array.isArray(urls) ? urls : []} />;
      }
      break;
    }

    case ApplicantOfferRequirementTask.EnglishLanguageCertificate: {
      const documentData = originalFulfilmentData as IDocumentFulfilmentData;
      if (documentData?.urls) {
        const linkText = generateLinkText(requirementId, application, originalFulfilmentData, taskText, taskType);
        const count = documentData?.urls?.length || 0;
        return (
          <PreviewLink
            preview={preview}
            urls={documentData.urls}
            text={linkText || 'English language certificate'}
            count={count}
          />
        );
      }

      const { text, subtext, linkText } = getEnglishLanguageCertificateFromApplication(application);
      if (linkText) {
        const urls = getFulfilmentProp(originalFulfilmentData, 'urls');
        return <PreviewLink preview={preview} urls={Array.isArray(urls) ? urls : []} text={linkText} />;
      }
      return <FormattedText text={text} subtext={subtext} />;
    }

    case ApplicantOfferRequirementTask.ProofOfManagerialExperience: {
      const documentData = originalFulfilmentData as IDocumentFulfilmentData;
      if (documentData?.urls) {
        const linkText = generateLinkText(requirementId, application, originalFulfilmentData, taskText, taskType);
        const count = documentData?.urls?.length || 0;
        return (
          <PreviewLink
            preview={preview}
            urls={documentData.urls}
            text={linkText || 'Proof of managerial experience'}
            count={count}
          />
        );
      }

      const proofOfManagerialExperience = getProofOfManagerialExperienceFromApplication(application);
      if (proofOfManagerialExperience) {
        return (
          <>
            {proofOfManagerialExperience.map(({ text, subtext }, index) => {
              return <FormattedText text={text} subtext={subtext} key={index} />;
            })}
          </>
        );
      }
      break;
    }

    case ApplicantOfferRequirementTask.RecordedInterview: {
      return (
        <RecordedInterviewFulfilmentRenderer
          fulfilmentData={originalFulfilmentData}
          requirementId={requirementId}
          application={application}
          openFile={preview}
          taskText={taskText}
        />
      );
    }

    case ApplicantOfferRequirementTask.RefereeDetails: {
      const referees = getFulfilmentProp(originalFulfilmentData, 'referees') as IReferee[];
      if (!referees || referees.length === 0) return null;
      return (
        <Box>
          {referees.map((referee, index) => {
            const relationship = getRefereeRelationshipText(referee);
            return (
              <Box key={`${referee.email}-${index}`}>
                {index > 0 && <Divider my={2} />}
                <Text fontSize="sm" mb={0} color="muted">
                  {relationship}: {referee.name},
                </Text>
                <Text fontSize="sm" mb={0} color="muted">
                  {referee.occupation} at {referee.organisation}
                </Text>
                <Text fontSize="sm" color="muted">
                  {referee.email}
                </Text>
              </Box>
            );
          })}
        </Box>
      );
    }
  }
};

const FormattedFulfilmentData = ({
  requirementId,
  taskType,
  application,
  preview,
  fulfilmentData: originalFulfilmentData,
  taskText,
  isMultiQualification,
}: {
  requirementId: ApplicantOfferRequirementTask | string;
  taskType?: ApplicantOfferRequirementTask;
  application: Application;
  preview: (urls: string[]) => void;
  fulfilmentData: IApplicantOfferRequirementTask['fulfilmentData'];
  taskText?: string;
  isMultiQualification?: boolean;
}): React.ReactElement | null | undefined => {
  const formattedFulfilmentData = useMemo(
    () =>
      getFormattedFulfilmentData(
        requirementId,
        application,
        originalFulfilmentData,
        preview,
        taskText,
        isMultiQualification,
        taskType,
      ),
    [requirementId, application, originalFulfilmentData, preview, taskText, isMultiQualification, taskType],
  );

  if (formattedFulfilmentData) {
    return formattedFulfilmentData;
  }

  const data = originalFulfilmentData as IDocumentFulfilmentData | ITextFulfilmentData;

  if ('urls' in data && data.urls) {
    const documentData = data as IDocumentFulfilmentData;
    const linkText = generateLinkText(requirementId, application, originalFulfilmentData, taskText, taskType);
    const count = documentData.urls?.length || 0;
    return (
      <PreviewLink
        preview={preview}
        urls={documentData.urls}
        text={linkText || documentData.sKey?.split('#').pop() || 'Document'}
        count={count}
      />
    );
  }

  if ('text' in data && data.text) {
    const textData = data as ITextFulfilmentData;
    return <FormattedText text={textData.text} subtext={textData.subtext} />;
  }

  return null;
};
export default FormattedFulfilmentData;
